import { useCallback, useMemo } from "react";
import Select, { components, CSSObjectWithLabel, GroupBase, OptionsOrGroups } from "react-select";

import styles from "./SelectControl.module.scss";

interface SelectProps<TOption> {
    options: OptionsOrGroups<ISelectControlOption<TOption>, GroupBase<ISelectControlOption<TOption>>>;
    value: TOption;
    onChange: (value: TOption) => void;
    useEmptyOption?: boolean;
    isInvalid?: boolean;
    maxMenuHeight?: number;
    isMenuOpen?: boolean;
    isClearable?: boolean;
    isSearchable?: boolean;
    defaultMenuIsOpen?: boolean;
}

interface ISelectControlOption<TOption> {
    label: string | JSX.Element;
    value: TOption;
}

function DropdownIndicatorIcon(): JSX.Element {
    return (
        <svg height="15" width="15" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
            <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z" />
        </svg>
    );
}

function SelectControl<TOption extends unknown>(props: SelectProps<TOption>): JSX.Element {
    const flatOptions: ISelectControlOption<TOption>[] = useMemo(
        () => {
            let options = [];
            if (props.options) {
                for (const option of props.options) {
                    if ("value" in option)
                        options.push(option);
                    else
                        options = [...options, ...option.options];
                }
            }
            return options;
        },
        [props.options]
    );

    const options = useMemo(
        () => {
            if (!props.useEmptyOption)
                return props.options;

            return [
                { label: (<div className={styles.emptyOption} />), value: "" } as ISelectControlOption<TOption>,
                ...props.options
            ];
        },
        [props.options, props.useEmptyOption]
    );

    const selected = useMemo(
        () => flatOptions.firstOrDefault(x => x.value === props.value),
        [flatOptions, props.value]
    );

    const customStyles = useMemo(
        () => ({
            control: (provided: CSSObjectWithLabel, state: { isFocused: boolean; }) => {
                if (!props.isInvalid) {
                    return {
                        ...provided,
                        borderColor: state.isFocused ? styles.borderColorFocused : styles.borderColorNormal,
                        "&:hover": state.isFocused ? styles.borderColorFocused : styles.borderColorNormal,
                        boxShadow: state.isFocused ? styles.borderBoxShadowFocused : "0"
                    } as CSSObjectWithLabel;
                }

                return {
                    ...provided,
                    borderColor: styles.colorBorderInvalid,
                    "&:hover": styles.colorBorderInvalid,
                    boxShadow: state.isFocused ? styles.borderBoxShadowFocused : "0"
                } as CSSObjectWithLabel;
            },
            dropdownIndicator: (provided: CSSObjectWithLabel) => ({
                ...provided,
                padding: "0 1px 0 0"
            } as CSSObjectWithLabel),
            menu: (provided: CSSObjectWithLabel) => ({
                ...provided,
                margin: 0,
                padding: 0
            } as CSSObjectWithLabel),
            indicatorSeparator: (provided: CSSObjectWithLabel) => ({
                ...provided,
                display: "none"
            } as CSSObjectWithLabel),
            option: (provided: any) => ({
                ...provided,
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap"
            } as CSSObjectWithLabel)
        }),
        [props.isInvalid]
    );

    const DropdownIndicator = useCallback(
        props => (
            <components.DropdownIndicator {...props}>
                <DropdownIndicatorIcon />
            </components.DropdownIndicator>
        ),
        []
    );

    return (
        <Select
            options={options}
            maxMenuHeight={props.maxMenuHeight}
            styles={customStyles}
            components={{ DropdownIndicator }}
            menuIsOpen={props.isMenuOpen}
            onChange={option => props.onChange(option?.value)}
            placeholder=""
            value={selected}
            isClearable={props.isClearable}
            isSearchable={props.isSearchable}
            defaultMenuIsOpen={props.defaultMenuIsOpen}
        />
    );
}

export { ISelectControlOption, SelectControl };
